import { useLayoutEffect } from "react";
import "../components/collectionRowFocus.css";

// Collection cells clip text visually; retain the entire rendered explanation
// on hover. Observe asynchronous text (e.g. Capacity) without changing values.
// Explicit domain tooltips always win. No tooltip/persistence state is shared.
export function useCollectionCellTitles(rootRef, selector, enabled = true) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) return undefined;
    let disposed = false;
    const observed = new Set();
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => update());
    const update = () => {
      if (disposed) return;
      const names = new Set(root.querySelectorAll("[data-collection-name]"));
      for (const name of observed) {
        if (!names.has(name)) { resize?.unobserve(name); observed.delete(name); }
      }
      for (const name of names) {
        if (!observed.has(name)) { resize?.observe(name); observed.add(name); }
        // This is the ellipsis-bearing element, not the expanding cell or
        // the name control (which also reserves room for an arrow).
        const title = name.scrollWidth > name.clientWidth
          ? name.textContent.replace(/\s+/g, " ").trim() : "";
        // An empty title also blocks native title inheritance.
        if (name.getAttribute("title") !== title) name.setAttribute("title", title);
      }
      for (const cell of root.querySelectorAll(selector)) {
        if (cell.hasAttribute("data-collection-identity")) {
          cell.removeAttribute("title");
          cell.removeAttribute("data-collection-title");
          continue;
        }
        if (cell.title && !cell.hasAttribute("data-collection-title")) continue;
        const text = cell.textContent.replace(/\s+/g, " ").trim();
        if (text) {
          cell.title = text;
          cell.setAttribute("data-collection-title", "");
        }
      }
    };
    update();
    const observer = typeof MutationObserver === "undefined" ? null : new MutationObserver(update);
    observer?.observe(root, { childList: true, characterData: true, subtree: true,
      attributes: true, attributeFilter: ["class", "style"] });
    window.addEventListener("resize", update);
    document.fonts?.addEventListener?.("loadingdone", update);
    document.fonts?.ready?.then(update);
    return () => {
      disposed = true;
      observer?.disconnect();
      resize?.disconnect();
      window.removeEventListener("resize", update);
      document.fonts?.removeEventListener?.("loadingdone", update);
    };
  });
}
